import type { Enemy } from "../entities/Enemy.ts";
import type { Tower } from "../entities/Tower.ts";
import type { EnemySpecialType, Element } from "../entities/types.ts";
import type { EventBus } from "../core/EventBus.ts";
import { ENEMY_SHIFT_PALETTE } from "../core/palette.ts";
import { JAM, AURA } from "../core/config.ts";

/** The resistance cycle for the `shiftResistance` special (S39). Relocated
 *  here verbatim from Enemy.ts — the registry now owns the shift table. */
const SHIFT_ELEMENTS = [
  "fire",
  "water",
  "lightning",
] as const satisfies readonly Element[];

/**
 * Context handed to the WaveManager-invoked behavior hooks (onPreUpdate /
 * onPostUpdate). Carries only what the relocated logic needs:
 *   - `bus`      — the event bus (the `boss.phaseChange` emit).
 *   - `spawnAdd` — the seam onto `WaveManager.spawnEnemy(type, true)`; the
 *                  `isAdd=true` flag preserves the "internal add, don't
 *                  re-mutate" contract (no split cascade).
 *
 *   - `enemyRng` — the isolated `ENEMY_SEED_SALT` sub-stream (the 5th, after
 *                  SPAWN/SHIELD/MODIFIER/BREACH), exposed for any FUTURE rolled
 *                  behaviour. UNCONSUMED by the 3 current (deterministic)
 *                  handlers, so an undrawn stream never advances → byte-identical.
 */
export interface EnemyBehaviorContext {
  bus: EventBus;
  spawnAdd: (type: string) => void;
  enemyRng: () => number;
  /** BL-152 (S154) — the live tower list (`gs.towers`), for the Jammer's
   *  tower-suppression hook. A STABLE array ref (mutated in place); empty in bare
   *  unit harnesses, so the Jammer hook is a no-op there. */
  towers: Tower[];
  /** BL-152 (S154) — the live active-enemy list (`enemyPool.active`), for the
   *  Overseer's force-multiplier aura hook. A STABLE array ref. */
  enemies: Enemy[];
}

/**
 * One enemy behavior's relocated logic. Each special owns at most one hook,
 * invoked from its ORIGINAL call site so the determinism contract (call order,
 * event emissions, spawn calls) stays byte-identical:
 *   - onTick:       per-enemy, from `Enemy.updateSpecials`      (shiftResistance)
 *   - onPreUpdate:  from `WaveManager.tickEnemies` BEFORE update (spawnAdds)
 *   - onPostUpdate: from `WaveManager.tickEnemies` AFTER  update (finalBoss)
 */
export interface EnemyBehaviorDef {
  onTick?: (enemy: Enemy, dt: number) => void;
  onPreUpdate?: (enemy: Enemy, ctx: EnemyBehaviorContext) => void;
  /** BL-155 (S158): gains an optional `dt` (the frame step) so a timer-driven
   *  post-update mechanic (the Leech's credit siphon) can accumulate. The live
   *  `WaveManager.tickEnemies` call always passes it; existing handlers
   *  (jammer/overseer/finalBoss) omit the param ⇒ byte-identical. */
  onPostUpdate?: (enemy: Enemy, ctx: EnemyBehaviorContext, dt?: number) => void;
  /** BL-155 (S158) — fires from `WaveManager.tickEnemies` when this enemy is
   *  KILLED (hp ≤ 0, NOT on a leak) — the seam the Splitter's death-spawn rides.
   *  Only `splitter` registers one, so every existing type stays byte-identical
   *  (the lookup is `undefined` for them). */
  onDeath?: (enemy: Enemy, ctx: EnemyBehaviorContext) => void;
}

/**
 * BL-151 / D-250 — the data-driven enemy-behavior registry, the enemy twin of
 * `MODULE_EFFECTS` (D-219) / `CARD_EFFECTS`. The 3 hand-wired specials are
 * lifted into typed handlers keyed by `EnemySpecialType`; the `if`-chains at
 * the call sites became O(1) lookups. Each handler is an EXACT relocation of
 * the old inline logic (same fields, same value expressions, same order, same
 * emissions, same `isAdd` spawn calls) — the three golden masters + the P0
 * nets prove it behavior-byte-identical. A new enemy MECHANIC is now one
 * registry entry + one `enemies.json` field (the BL-152 unlock).
 */
export const ENEMY_BEHAVIORS: Record<EnemySpecialType, EnemyBehaviorDef> = {
  // Relocated from `Enemy.updateSpecials`: the timer cycles resistance through
  // SHIFT_ELEMENTS and recolours from ENEMY_SHIFT_PALETTE (deterministic).
  shiftResistance: {
    onTick: (enemy, dt) => {
      const special = enemy.special;
      if (!special?.interval) return;
      enemy.shiftTimer += dt;
      if (enemy.shiftTimer >= special.interval) {
        enemy.shiftTimer = 0;
        const currentIdx = enemy.resistance
          ? SHIFT_ELEMENTS.findIndex((el) => el === enemy.resistance)
          : -1;
        const nextIdx = (currentIdx + 1) % SHIFT_ELEMENTS.length;
        enemy.resistance = SHIFT_ELEMENTS[nextIdx] ?? "fire";
        enemy.color = ENEMY_SHIFT_PALETTE[enemy.resistance] ?? enemy.color;
      }
    },
  },

  // Relocated from `WaveManager.tickEnemies` (pre-update block). The truthy
  // `spawnThreshold` gate fires the adds once HP crosses the threshold (the boss
  // / Core Breaker path); the `!addSpawned` guard makes the spawn fire exactly
  // once. (BL-165, S160: the old `corrupted_node spawnThreshold:0` quirk is gone —
  // `corrupted_node` carries no `special` in current data.)
  spawnAdds: {
    onPreUpdate: (enemy, ctx) => {
      const special = enemy.special;
      if (!special) return;
      if (
        !enemy.addSpawned &&
        special.spawnThreshold &&
        enemy.hp / enemy.maxHp <= special.spawnThreshold
      ) {
        enemy.addSpawned = true;
        for (let a = 0; a < (special.spawnCount ?? 0); a++) {
          ctx.spawnAdd(special.addType ?? "swarm");
        }
      }
    },
  },

  // Relocated from `WaveManager.tickEnemies` (post-update block). Per-phase, in
  // the exact original order: emit `boss.phaseChange`, spawn
  // `spawnAddsPerPhase[phase-1]` × `spawnCount`, set speed 1.5×(P2)/0.5×(P3),
  // null the resistance at P3.
  finalBoss: {
    onPostUpdate: (enemy, ctx) => {
      const newPhase = enemy.checkPhaseTransition();
      if (newPhase !== null && newPhase !== enemy.currentPhase) {
        enemy.currentPhase = newPhase;
        ctx.bus.emit("boss.phaseChange", { boss: enemy, phase: newPhase });
        // Spawn adds for this phase. INDEX SEMANTICS (BL-166): the array is
        // 0-based, but a transition only ever fires INTO phase 2 or 3 (phase 1 is
        // the boss's spawn state — `checkPhaseTransition` never returns it), so
        // `newPhase-1` reads index 1 (phase 2) and index 2 (phase 3). Index 0 is
        // therefore INTENTIONALLY INERT — never spawned. W35 AXIOM-77 lists "scout"
        // at `spawnAddsPerPhase[0]` (enemies.json) as the phase-1 roster note; do
        // NOT "fix" it into a [0]→phase-2 read — that would silently re-balance the
        // finale. The FinalBoss-cascade test locks this (only phase-2/3 adds fire).
        const addTypes = enemy.special?.spawnAddsPerPhase;
        const addType = addTypes?.[newPhase - 1] ?? "swarm";
        const count = enemy.special?.spawnCount ?? 5;
        for (let a = 0; a < count; a++) {
          ctx.spawnAdd(addType);
        }
        // Phase-specific speed modifier
        if (newPhase === 2) enemy.speed = enemy.baseSpeed * 1.5;
        if (newPhase === 3) {
          enemy.speed = enemy.baseSpeed * 0.5;
          enemy.resistance = null;
        }
      }
    },
  },

  // ── BL-152 (S154) — the Act II JAMMER (the first enemy that attacks your
  // DEFENSE). Each tick it lives, it stamps every tower within `jamRadius` with
  // JAM.WINDOW_MS, putting them on "slow-time" (CombatSystem reads the stamp).
  // Counter-play: kill it (the stamp stops refreshing → towers recover) or spread
  // your towers out of one field. Deterministic radius mechanic — draws NO
  // `enemyRng` (the prompt's "prefer deterministic" steer; the seam stays unused).
  jammer: {
    onPostUpdate: (enemy, ctx) => {
      const radius = enemy.special?.jamRadius;
      if (!radius) return;
      // S156 / PT-41 — the suppression strength + window are DATA now
      // (`enemies.json` `special`), beside the radius; absent → the config
      // default (byte-safe). One archetype row carries its whole tuning.
      const windowMs = enemy.special?.windowMs ?? JAM.WINDOW_MS;
      const fireRateMult = enemy.special?.fireRateMult ?? JAM.FIRE_RATE_MULT;
      const rSq = radius * radius;
      for (const tower of ctx.towers) {
        const dx = tower.x - enemy.x;
        const dy = tower.y - enemy.y;
        if (dx * dx + dy * dy <= rSq) {
          tower.jammedTimer = windowMs;
          tower.jammedFireRateMult = fireRateMult;
        }
      }
    },
  },

  // ── BL-152 (S154) — the Act III OVERSEER (Aura-Host). Each tick it lives, it
  // stamps every NON-overseer enemy within `auraRadius` with AURA.WINDOW_MS — a
  // bounded incoming-damage mitigation (Enemy.applyDamage reads the stamp). When
  // the host dies the stamp stops → the buff decays → the aura COLLAPSES (the
  // named counter-play: focus-fire the host). Overseers never buff each other, so
  // every host stays individually killable. Deterministic — draws NO `enemyRng`.
  overseer: {
    onPostUpdate: (enemy, ctx) => {
      const radius = enemy.special?.auraRadius;
      if (!radius) return;
      // S156 / PT-41 — the aura mitigation strength + window are DATA now,
      // beside the radius; absent → the config default (byte-safe).
      const windowMs = enemy.special?.windowMs ?? AURA.WINDOW_MS;
      const damageTakenMult =
        enemy.special?.damageTakenMult ?? AURA.DAMAGE_TAKEN_MULT;
      const rSq = radius * radius;
      for (const other of ctx.enemies) {
        if (other === enemy || !other.active) continue;
        if (other.special?.type === "overseer") continue;
        const dx = other.x - enemy.x;
        const dy = other.y - enemy.y;
        if (dx * dx + dy * dy <= rSq) {
          other.auraBuffTimer = windowMs;
          other.auraDamageMult = damageTakenMult;
        }
      }
    },
  },

  // ── BL-155 (S158) — the Act II LEECH. Every `interval` ms it lives, it siphons
  // a bounded `drainAmount` of the player's CREDITS (via the `enemy.leech` emit
  // the EconomySystem subtracts, clamped at 0), capped at `drainCap` total per
  // instance so it can NEVER bankrupt you unfairly. Punishes HOARDING (ties to
  // the S156 economy). Counter-play: kill it before it bleeds you, or spend down
  // your hoard. Deterministic timer — draws NO `enemyRng`. The credit subtraction
  // is a sim write (the re-baselined fixtures witness the moved gold).
  leech: {
    onPostUpdate: (enemy, ctx, dt = 0) => {
      const s = enemy.special;
      const amount = s?.drainAmount ?? 0;
      if (amount <= 0) return;
      enemy.leechTimer += dt;
      const interval = s?.interval ?? 1000;
      if (enemy.leechTimer < interval) return;
      enemy.leechTimer -= interval;
      const take = Math.min(amount, (s?.drainCap ?? Infinity) - enemy.leechDrained);
      if (take <= 0) return;
      enemy.leechDrained += take;
      ctx.bus.emit("enemy.leech", { enemy, amount: take });
    },
  },

  // ── BL-155 (S158) — the Act III REGENERATOR. Each tick it heals `regenFraction`
  // of maxHp per second, SHIELD-then-HP, bounded to the maxima (never overheals).
  // Immortal under low DPS → punishes spread-thin / low-DPS builds; the counter
  // is burst + focus (out-damage the heal). Deterministic — draws NO `enemyRng`;
  // the heal is a sim write (the re-baselined fixtures witness the kill-timing).
  regenerator: {
    onTick: (enemy, dt) => {
      const frac = enemy.special?.regenFraction;
      if (!frac) return;
      // Task-009 (Spec B) — FROZEN SOLID DOES NOT KNIT. A fully frozen
      // regenerator (slow ×0 — water's flash-freeze/chain-freeze/gold nova)
      // pauses its heal. Without this the two shipped mechanics DEADLOCK
      // into a production-reachable softlock: a water tower re-freezes on
      // every hit (fireRate 1.4/s vs 700-1100ms freezes ≈ 100% uptime), and
      // if its DPS is below the regen rate the enemy heals to cap at speed 0
      // forever — the wave can never end (no wave timeout exists). Caught by
      // the probe lane on an INTERMEDIATE Task-009 data draft
      // (adaptive-competent @ seed 0xda7a003 stalled W26 with ONE alive
      // enemy: hp full, path intact, speed 0); the SHIPPED data does not
      // happen to reproduce it (verified: the probe completes with or
      // without this guard), so this is a prophylactic guard against a
      // production-REACHABLE configuration, not the fix for a currently
      // failing run. Partial slows (multiplier > 0) heal normally — only the
      // hard freeze pauses.
      if (enemy.slowTimer > 0 && enemy.slowMultiplier === 0) return;
      let heal = enemy.maxHp * frac * (dt / 1000);
      if (enemy.shieldMax > 0 && enemy.shieldHp < enemy.shieldMax) {
        const s = Math.min(enemy.shieldMax - enemy.shieldHp, heal);
        enemy.shieldHp += s;
        heal -= s;
      }
      if (heal > 0 && enemy.hp < enemy.maxHp) {
        enemy.hp = Math.min(enemy.maxHp, enemy.hp + heal);
      }
    },
  },

  // ── BL-155 (S158) — the Act II/III SPLITTER. On DEATH (kill, not leak) it
  // fractures into `spawnCount` fast `addType` sub-enemies via the `spawnAdd`
  // seam (isAdd=true ⇒ no re-mutate, no cascade). Punishes single-target / weak
  // targeting → forces AREA damage. Counter-play: AoE the splits (or let it leak,
  // a bounded life-for-no-split trade). Deterministic — draws NO `enemyRng`.
  splitter: {
    onDeath: (enemy, ctx) => {
      const s = enemy.special;
      if (!s) return;
      const type = s.addType ?? "swarm";
      for (let i = 0; i < (s.spawnCount ?? 0); i++) ctx.spawnAdd(type);
    },
  },
};
